import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Check, Loader2, Image as ImageIcon, Film, Music, FileText } from "lucide-react";
import { api } from "@/api/client";

// Shared Vault/gallery picker for the ALIX & JIM chats. Fetches the operator's
// gallery + project scenes from `endpoint` and lets them multi-select assets to
// feed into the chat (for review or stitching). Returns refs
// [{ asset_id, kind, name, url }] via onPick.
const KIND_FOR_TRACK = { image: "image", video: "video", music: "audio", writing: "text" };

function Thumb({ url }) {
  const [src, setSrc] = useState(null);
  const path = (url || "").replace(/^\/api/, "");
  useEffect(() => {
    let obj;
    if (path) {
      api.get(path, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
        .catch(() => {});
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  return src
    ? <img src={src} alt="" className="w-full h-full object-cover pointer-events-none" />
    : <div className="w-full h-full grid place-items-center bg-black/40 pointer-events-none"><Loader2 size={14} className="text-gold animate-spin" /></div>;
}

// Hard-bounded, non-image preview (video / document). Never derives height from
// content — the parent card owns the fixed ratio.
function FilePlaceholder({ it }) {
  const K = it.kind === "video" ? Film : it.kind === "audio" ? Music : it.kind === "text" ? FileText : FileText;
  const n = it.name || "";
  const dot = n.lastIndexOf(".");
  const ext = (dot > 0 && dot < n.length - 1)
    ? n.slice(dot + 1).toUpperCase().slice(0, 4)
    : ({ video: "MP4", audio: "MP3", text: "TXT", image: "IMG" }[it.kind] || "FILE");
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 bg-charcoal pointer-events-none">
      <K size={22} className="text-gold/70" strokeWidth={2.4} />
      <span className="border-2 border-black bg-gold px-1.5 py-0.5 font-mono text-[9px] font-black tracking-widest text-black">{ext}</span>
    </div>
  );
}

export default function VaultPicker({ endpoint = "/alix/context/vault", onPick, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({}); // asset_id -> ref

  useEffect(() => {
    api.get(endpoint)
      .then((r) => {
        const gallery = (r.data?.gallery || []).map((a) => ({
          asset_id: a.asset_id,
          kind: KIND_FOR_TRACK[a.track] || "file",
          name: a.title || a.filename || "asset",
          url: a.url,
          track: a.track,
        }));
        setItems(gallery);
      })
      .catch(() => toast.error("Couldn't load your Vault"))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const toggle = (it) => {
    setSel((s) => {
      const n = { ...s };
      if (n[it.asset_id]) delete n[it.asset_id];
      else n[it.asset_id] = it;
      return n;
    });
  };

  const confirm = () => {
    const picks = Object.values(sel);
    if (!picks.length) { toast.error("Select at least one asset"); return; }
    onPick?.(picks);
    onClose?.();
  };

  const count = Object.keys(sel).length;

  return (
    // Backdrop — solid dark, NO blur. Centres the clamped modal inside the S20 chassis.
    <div className="absolute inset-0 z-[60] flex items-center justify-center p-3 bg-black/70"
      data-testid="vault-picker" onClick={onClose}>
      {/* Modal shell — permanently clamped, overflow-hidden, strict flex column. */}
      <div
        className="w-full max-w-[412px] h-[82dvh] max-h-[640px] flex flex-col overflow-hidden rounded-2xl border-2 border-black bg-charcoal shadow-[6px_6px_0px_#000]"
        data-testid="vault-picker-shell"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER — fixed */}
        <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b-2 border-black bg-charcoal">
          <div className="leading-tight">
            <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-gold/60">From your Vault</p>
            <h3 className="font-mono text-base font-black tracking-widest text-gold">PICK ASSETS</h3>
          </div>
          <button onClick={onClose} data-testid="vault-picker-close" aria-label="Close"
            className="w-9 h-9 grid place-items-center border-2 border-black bg-gold text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
            <X size={17} strokeWidth={2.6} />
          </button>
        </header>

        {/* SCROLLABLE ASSET REGION — the ONLY scroll area */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar" data-testid="vault-picker-scroll">
          {loading ? (
            <div className="h-full grid place-items-center"><Loader2 className="text-gold animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="h-full grid place-items-center px-8 text-center">
              <p className="font-mono text-[12px] text-white/50">Your Vault is empty — create something first, then I can review it.</p>
            </div>
          ) : (
            <div className="p-3 grid grid-cols-3 gap-2" data-testid="vault-picker-grid">
              {items.map((it) => {
                const active = !!sel[it.asset_id];
                return (
                  <button key={it.asset_id} onClick={() => toggle(it)} data-testid={`vault-pick-${it.asset_id}`}
                    className={`relative aspect-[3/4] overflow-hidden rounded-lg border-2 bg-black/60 ${active ? "border-gold shadow-[3px_3px_0px_#D4AF37]" : "border-black shadow-[2px_2px_0px_#000]"}`}>
                    {it.kind === "image" ? <Thumb url={it.url} /> : <FilePlaceholder it={it} />}
                    <span className="absolute bottom-0 inset-x-0 bg-black/80 px-1 py-0.5 font-mono text-[7px] uppercase tracking-wide text-white/85 truncate pointer-events-none">{it.name}</span>
                    {active && (
                      <span className="absolute top-1 right-1 w-5 h-5 grid place-items-center border-2 border-black bg-gold text-black">
                        <Check size={11} strokeWidth={3.5} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* PINNED BOTTOM ACTION DOCK — never scrolls, safe-area clearance */}
        <footer className="shrink-0 px-3 pt-3 border-t-2 border-black bg-charcoal"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <button onClick={confirm} disabled={count === 0} data-testid="vault-picker-confirm"
            className="w-full flex items-center justify-center gap-2 border-2 border-black bg-gold text-black font-mono text-[11px] font-black uppercase tracking-widest px-3 py-3 rounded-xl shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
            <Check size={14} strokeWidth={3} /> Add {count > 0 ? count : ""} {count === 1 ? "asset" : "assets"}
          </button>
        </footer>
      </div>
    </div>
  );
}
